import React, { useState } from "react";
import PharmacySearch from "./PharmacySearch";
import { saveMedicalPharmacy, pharmacyAnswerText } from "../../utils/medicalProfileApi";
import { useSubmitGuard } from "../../utils/useSubmitGuard";

/**
 * The pharmacy search on the intake form's "What is the name and location of
 * your preferred pharmacy?" question.
 *
 * Choosing a result fills in the answer and saves the pharmacy to the patient's
 * profile on its own, with its phone and fax for the provider. If that save fails
 * the answer is still filled in, and the server keeps it as typed text when the
 * form is submitted, so nothing is lost: the patient can always type instead.
 */
const IntakePharmacyPicker = ({ patientId, onChosen, onSaving = () => {} }) => {
  const [chosen, setChosen] = useState(null);
  const [saved, setSaved] = useState(false);
  const [, guard] = useSubmitGuard();

  const choose = guard(async (pharmacy) => {
    setChosen(pharmacy);
    setSaved(false);
    onChosen(pharmacyAnswerText(pharmacy));
    if (!patientId) return;
    // The form waits for this: submitted mid-save, the intake's own copy to the
    // profile and this save could each overwrite the other's fields.
    onSaving(true);
    try {
      await saveMedicalPharmacy(patientId, pharmacy);
      setSaved(true);
    } catch (err) {
      // The answer above still carries it; the profile picks it up on submit.
    } finally {
      onSaving(false);
    }
  });

  return (
    <div style={{ marginBottom: "12px", textAlign: "left" }}>
      <p style={{ margin: "0 0 8px", fontSize: "0.875rem", color: "var(--youro-charcoal)" }}>
        Find your pharmacy, or type its name and address below.
      </p>
      <PharmacySearch onSelect={choose} />
      {chosen && (
        <p role="status" style={{ margin: "10px 0 0", fontSize: "0.875rem", color: "var(--youro-charcoal)" }}>
          <strong>{chosen.name}</strong> {saved ? "is saved as your preferred pharmacy." : "is filled in below."}
        </p>
      )}
    </div>
  );
};

export default IntakePharmacyPicker;
